import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Activity, CheckCircle, Shield, AlertTriangle, ArrowRight } from 'lucide-react';

export default function SelfIdentifyPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Welcome, 2: Verification, 3: Form
  const [projectInfo, setProjectInfo] = useState(null);
  const [segments, setSegments] = useState([]);
  const [humanCheck, setHumanCheck] = useState({ answer: '', correct: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    proposed_segment_ids: [],
    honeypot: '', // Hidden field for bot detection
  });

  // Simple math challenge for human verification
  const [mathChallenge] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await publicAPI.getSegments(projectId);
        setProjectInfo(response.data.project);
        setSegments(response.data.segments);
      } catch (err) {
        toast.error('Project not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  const handleVerification = () => {
    if (parseInt(humanCheck.answer) === mathChallenge.answer) {
      setHumanCheck(prev => ({ ...prev, correct: true }));
      setStep(3);
    } else {
      toast.error('Incorrect answer. Please try again.');
    }
  };

  const toggleSegment = (segmentId) => {
    setFormData(prev => ({
      ...prev,
      proposed_segment_ids: prev.proposed_segment_ids.includes(segmentId)
        ? prev.proposed_segment_ids.filter(id => id !== segmentId)
        : [...prev.proposed_segment_ids, segmentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.proposed_segment_ids.length === 0) {
      toast.error('Please select at least one segment');
      return;
    }

    setSubmitting(true);
    try {
      await publicAPI.selfIdentify(projectId, formData);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <Skeleton className="h-64" />
        </Card>
      </div>
    );
  }

  if (!projectInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="py-16 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Project Not Found</h2>
            <p className="text-slate-500">The project you're looking for doesn't exist.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Registration Submitted
            </h2>
            <p className="text-slate-600 mb-2">
              Thank you for registering as a stakeholder for {projectInfo.name}.
            </p>
            <p className="text-slate-500 text-sm">
              Please check your email to verify your address. Once verified, an administrator 
              will review your registration and you'll receive a survey invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-8">
        <img 
          src="/logo.png" 
          alt="Pulse by ProjectAI" 
          className="h-24 object-contain"
        />
      </div>

      <Card className="max-w-lg w-full shadow-lg">
        {/* Step 1: Welcome */}
        {step === 1 && (
          <>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Welcome to {projectInfo.name}
              </CardTitle>
              <CardDescription className="mt-2">
                We value your perspective as a stakeholder. Your feedback helps us 
                understand sentiment and improve our project outcomes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-3 p-4 bg-[#5AA2AE]/5 rounded-lg">
                <Shield className="w-5 h-5 text-[#5AA2AE] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <strong>Your privacy matters.</strong> All responses are kept confidential 
                  and results are only shown in aggregate (minimum 3 responses per group).
                </div>
              </div>
              <Button 
                className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={() => setStep(2)}
                data-testid="continue-to-verify-btn"
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 2: Human Verification */}
        {step === 2 && (
          <>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Quick Verification
              </CardTitle>
              <CardDescription>
                Please answer this simple question to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-lg text-slate-700 mb-4">
                  What is <strong>{mathChallenge.a} + {mathChallenge.b}</strong>?
                </p>
                <Input
                  type="number"
                  placeholder="Your answer"
                  value={humanCheck.answer}
                  onChange={(e) => setHumanCheck(prev => ({ ...prev, answer: e.target.value }))}
                  className="max-w-32 mx-auto text-center text-lg"
                  data-testid="human-check-input"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                  onClick={handleVerification}
                  data-testid="verify-btn"
                >
                  Verify
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {/* Step 3: Registration Form */}
        {step === 3 && (
          <>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Register as a Stakeholder
              </CardTitle>
              <CardDescription>
                Tell us a bit about yourself so we can send you relevant questions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot - hidden from users */}
                <input
                  type="text"
                  name="website"
                  value={formData.honeypot}
                  onChange={(e) => setFormData(prev => ({ ...prev, honeypot: e.target.value }))}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      data-testid="self-id-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      data-testid="self-id-lastname-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="self-id-email-input"
                  />
                  <p className="text-xs text-slate-500">
                    We'll send a verification email to confirm your address
                  </p>
                </div>

                {segments.length > 0 && (
                  <div className="space-y-2">
                    <Label>Which group(s) best describe you?</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                      {segments.map(segment => (
                        <div key={segment.id} className="flex items-start gap-2">
                          <Checkbox
                            id={`seg-${segment.id}`}
                            checked={formData.proposed_segment_ids.includes(segment.id)}
                            onCheckedChange={() => toggleSegment(segment.id)}
                          />
                          <div>
                            <Label 
                              htmlFor={`seg-${segment.id}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {segment.name}
                            </Label>
                            {segment.description && (
                              <p className="text-xs text-slate-500">{segment.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                  disabled={submitting}
                  data-testid="self-id-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Register'}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
